import { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'

import { Container } from '@awaze/design-system.components.container'
import { SearchControls } from '@awaze/design-system.compositions.search-controls'
import {
  SearchForm as SearchFormBase,
  SearchFormProps,
} from '@awaze/design-system.compositions.search-form'
import type { SearchFormProps as SearchFormPropsGD73 } from '@awaze/design-system.compositions.search-form.gd-73'
import { SearchForm as SearchFormGD73 } from '@awaze/design-system.compositions.search-form.gd-73'
import {
  GoogleSearchMethods,
  useFetchDestination,
  useFetchDestinationForLegacyList,
  useResolveGooglePlacesID,
} from '@awaze/mfe-common.api.places-api'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  SORT_ORDER_UPDATED,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import { TmsContext } from '@awaze/mfe-common.contexts.tms'

import { StickableHeaderExtended } from 'src/components/MobileSticky/StickableHeaderExtended'
import {
  resolvePlacesId,
  resolveRadiusCentre,
} from 'src/components/SearchForm/SearchForm.helper'
import { SearchFormSubmitData } from 'src/components/SearchForm/SearchForm.types'
import {
  CORRELATION_ID_COOKIE_NAME,
  GD_73_RADIUS_KM,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
  SORT_OPTIONS_ENUM,
} from 'src/constants'
import {
  useAppBrandConfig,
  useAppConfig,
} from 'src/context/app-config-provider'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'
import { useApplicationConfig } from 'src/lib/applicationConfig'
import { addBoatsToDestinationList } from 'src/lib/contentful/boats-option-data'
import { useDisplayContext } from 'src/lib/display/displayContext'
import { Assert } from 'src/utils/assert'
import { setDisplayModeWrapper } from 'src/utils/displayModeOnClickHelper'
import { getCookie } from 'src/utils/helpers'
import { isNovasolPoweredBrand } from 'src/utils/isNovasolPoweredBrand'
import { useUpdatedPlacesConfig } from 'src/utils/optimizely/experiments'
import { useGoogleFallback } from 'src/utils/optimizely/experiments/use-google-fallback'
import { trackClientSideOptimizelyEvent } from 'src/utils/optimizely/trackOptimizelyEvent'
import {
  getSortOptionsFromConfig,
  shouldDisplaySortOptions,
} from 'src/utils/sortOptions/sortOptions'

import {
  MobileStickableContent,
  MobileSticky,
} from '../MobileSticky/MobileSticky'

import { usePlacesSuggestions } from './hooks/usePlacesSuggestions'
import { useSearchSelectionEvent } from './hooks/useSearchSelectionEvent'
import { getAutoSuggestSearch } from './getAutoSuggestSearch'
import {
  getHardcodedDestinationData,
  getMaxAmountOfNights,
  handleSearchFormSubmit,
  handleSearchFormSummaryClick,
} from './SearchForm.utils'
import { handleNearbyPropertiesClick } from './SearchForm.utils.gd-73'

type SearchFormComponentProps = {
  defaultConfig: SearchFormProps['defaultConfig']
  translations: SearchFormProps['translations']
  initialValues: SearchFormProps['initialValues'] & {
    destinationId?: number
  }
  locale: SearchFormProps['locale']
  openFilters: () => void
  marketCode: string
  mobileStickyMode?: boolean
  placesConfig?: {
    useLegacyDestinationSelector: boolean
    initialParentDestinationList: string | undefined
    pageSize: number | undefined
    pageSizePopular: number | undefined
    googleFallbackMethod: GoogleSearchMethods
  }
  isGuestApp?: boolean
}

const placesConfigLiteral: SearchFormComponentProps['placesConfig'] = {
  useLegacyDestinationSelector: false,
  initialParentDestinationList: undefined,
  pageSize: 20,
  pageSizePopular: 20,
  googleFallbackMethod: undefined,
}

export const SearchFormComponent: React.FC<SearchFormComponentProps> = ({
  defaultConfig,
  initialValues,
  openFilters,
  translations,
  locale,
  marketCode,
  mobileStickyMode = true,
  placesConfig = placesConfigLiteral,
  isGuestApp,
}: SearchFormComponentProps) => {
  // XP-1396 Determine which Google fallback method to use for the given brand
  if (placesConfig === null) {
    throw new Error('googleFallbackMethod is not defined in placesConfig')
  }
  const { formatMessage } = useIntlContext()
  const { brand } = useApplicationConfig()
  const tms = useContext(TmsContext)
  const [searchFormClicked, setSearchFormClicked] = useState(false)

  const { searchCriteria, totalSelectedFilters, hasResults } =
    useSearchContextState()

  const {
    handleDestinationSelection,
    handleDateSelection,
    handleGuestSelection,
  } = useSearchSelectionEvent()

  const appConfig = useAppConfig()

  Assert.defined(searchCriteria, 'searchCriteria is not defined')
  Assert.defined(defaultConfig, 'defaultConfig is undefined')

  const { start, accommodationType, sort, regionName, ne, sw, placesId } =
    searchCriteria

  const dispatch = useSearchContextDispatch()
  const optimizelyDecisionsContext = useOptimizelyDecisionsContext()

  const appBrandConfig = useAppBrandConfig(brand)

  placesConfig.useLegacyDestinationSelector =
    appBrandConfig.useLegacyDestinationSelector ?? false

  const googleSearchMethod = useGoogleFallback(brand)

  if (googleSearchMethod) {
    placesConfig.googleFallbackMethod = googleSearchMethod
  }

  // Modify the placesConfig based on the Optimizely flags
  placesConfig = useUpdatedPlacesConfig(brand, locale, placesConfig)

  const {
    useLegacyDestinationSelector,
    initialParentDestinationList,
    pageSize,
    pageSizePopular,
    googleFallbackMethod,
  } = placesConfig

  // Places-API Google Coordinates (radiusCentre) fetch
  const [googleID, setGoogleID] = useState<undefined | string>('')
  const radiusCentre = useResolveGooglePlacesID(googleID)

  // Optimizely XP: ACQ-1258 Use Places-API Legacy-List data
  const [fetchPlacesLegacyList, setFetchPlacesLegacyList] = useState(false)

  const placesLegacyListData = useFetchDestinationForLegacyList(
    fetchPlacesLegacyList,
  )

  // GD-73: Drive Time Search
  let SearchForm = SearchFormBase as typeof SearchFormGD73
  const optiContext = useOptimizelyDecisionsContext()

  const driveTimeSearchEnabled = optiContext?.isExperimentVariationKeyOneOf(
    OPTIMIZELY_FLAGS_ENUM.GD_73_DRIVE_TIME_SEARCH,
    ['variant_a', 'variant_b'],
  )
  const driveTimeSearchVariant = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.GD_73_DRIVE_TIME_SEARCH,
  )

  // GD-163: FlexiDate Search V2
  const freeDateSelectionEnabled =
    optiContext?.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.GD_163_FLEXIDATE_SEARCH_V2,
    ) === 'variant_a'

  if (driveTimeSearchEnabled) {
    SearchForm = SearchFormGD73
    translations = {
      ...translations,
      destination: {
        ...translations?.destination,
        nearby: {
          ctaTitle: formatMessage({
            id: 'nearby.ctaTitle',
            defaultMessage: 'Properties Nearby',
          }),
          ctaDescription: formatMessage({
            id: 'nearby.ctaDescription',
            defaultMessage: 'Discover properties around you',
          }),
          destinationField: formatMessage({
            id: 'nearby.destinationField',
            defaultMessage: 'Search nearby',
          }),
          geolocationPermissionWarning: formatMessage({
            id: 'nearby.geolocationPermissionWarning',
            defaultMessage:
              'You must allow your browser location request to view nearby properties.',
          }),
        },
      },
    } as SearchFormPropsGD73['translations']
  }

  const warningLogger = (message: string) => {
    const newrelicDefined = typeof newrelic !== 'undefined'
    const correlationId = getCookie(CORRELATION_ID_COOKIE_NAME) ?? ''
    const useNewRelicLogger = newrelicDefined && correlationId !== ''

    // If the NewRelic browser object is defined and they have a correlationId, log the warning to NewRelic
    return useNewRelicLogger
      ? newrelic.noticeError(message, {
          correlationId,
        })
      : // eslint-disable-next-line no-console
        console.warn(message)
  }

  useEffect(() => {
    setFetchPlacesLegacyList(searchFormClicked && useLegacyDestinationSelector)
  }, [searchFormClicked])

  if (brand === 'hoseasons') {
    addBoatsToDestinationList(defaultConfig)
  }

  const getSuggestions = usePlacesSuggestions(googleFallbackMethod)
  const originalAutoSuggestSearch = getAutoSuggestSearch(
    brand,
    marketCode,
    appConfig,
  )

  const getDestination = useFetchDestination()
  const searchParams = useSearchParams()

  const searchForm = (stuck: boolean) => (
    <div
      onClick={() => {
        if (!searchFormClicked) {
          setSearchFormClicked(() => true)
        }
      }}
    >
      <SearchForm
        defaultConfig={{
          ...defaultConfig,
          showDurationInput: !freeDateSelectionEnabled,
          autocompleteDebounceWait: 600,
          maxAmountOfNights: getMaxAmountOfNights({
            freeDateSelectionEnabled,
            brand,
          }),
          destinationListConfig: {
            ...(initialParentDestinationList && {
              initialParent: initialParentDestinationList,
            }),
            destinationFetch: async (locale, id, accommodationType) => {
              const idStringified = id?.toString()
              const destinationId =
                idStringified === 'countries' ? undefined : idStringified
              const destinations =
                accommodationType !== 'boat-holidays'
                  ? getDestination(destinationId, true)
                  : getHardcodedDestinationData(locale, idStringified)

              return await destinations
            },
            pageSize,
            pageSizePopular,
            fetchDelay: 0,
            sortAlphabetically: true,
          },
        }}
        autoSuggestSearch={async (searchTerm, accommodationType) => {
          // `searchTerm` eventually ends up being passed to the ApolloClient
          // the typing on the Appollo client package handles `string | null`
          // without any complains so `getSuggestions` should prob be typed as `string | null`
          const placesResult = await getSuggestions(searchTerm as string)

          if (
            placesResult?.[0]?.type === 'GooglePlacesApiAutocomplete' ||
            placesResult.length === 0
          ) {
            tms.updateDataLayer('link', 'search-no destination result', {
              search_term: searchTerm,
            })
          }

          // Ignore Places logic if Accommodation-Type equals 'Boats'
          const forceLocationLookupIfBoats =
            accommodationType === 'boat-holidays'

          const originalResult = await originalAutoSuggestSearch(
            // If this is undefined, a URLSearchParam() will be constructed with
            // an undefined - which is a build error but I don't think its going to
            // have an adverse runtime effect so probably fine to change the upstream types
            // To `string | undefined`
            searchTerm as string,

            // Undefined is explicitly handled inside this function so the type could be changed to
            // `string | undefined`
            accommodationType as string,

            !forceLocationLookupIfBoats,
          )

          if (!forceLocationLookupIfBoats) {
            return [
              ...placesResult,
              ...originalResult.filter((r) => r.property),
            ]
          }

          return originalResult
        }}
        initialValues={
          {
            ...initialValues,
            destination: regionName,
            placesId,
          } as SearchFormProps['initialValues']
        }
        isSummary={true}
        isSummaryFloating={!stuck}
        translations={translations as SearchFormPropsGD73['translations']}
        onNearbyPropertiesClick={() =>
          handleNearbyPropertiesClick({
            warningLogger,
            sendOptimizelyEvent: (eventKey) => {
              trackClientSideOptimizelyEvent(
                eventKey,
                optimizelyDecisionsContext,
              )
            },
          })
        }
        // I removed the more specific type (SearchFormSubmitData) because it causes a type mismatch
        // To make this more specific without the error, move the more specific type
        // into the design system
        onSubmit={(formData) => {
          delete formData.featureId
          const {
            destinationId,
            id,
            placesId,
            destination,
            radiusCentre: formRadiusCentre,
          } = formData

          const shouldSetCoordinateParams = !destinationId && ne && sw
          const overrideDestinationURL =
            destinationId && isNovasolPoweredBrand(brand)
          const catalogue = searchParams.get('catalogue')
          const radiusCentreDestination = destination?.length
            ? resolveRadiusCentre(
                radiusCentre,
                id,
                placesId,
                destinationId,
                searchParams.get('radiusCentre'),
              )
            : null
          const displayMode =
            driveTimeSearchVariant === 'variant_b' ? 'MAP' : 'LIST'

          const formDataWithDestinationURL = {
            ...formData,
            ...(formRadiusCentre && {
              displayMode,
            }),
            ...(catalogue && { catalogue }),
            placesId: resolvePlacesId(id, placesId, destination),
            radiusCentre: formRadiusCentre ?? radiusCentreDestination,
            ...(formRadiusCentre && {
              radiusKm: GD_73_RADIUS_KM,
            }),
            ...(overrideDestinationURL && {
              destinationURL: `/${formData.destinationId?.toString()?.replaceAll('_', '/')}`,
            }),
            ...(shouldSetCoordinateParams && {
              ne,
              sw,
            }),
            // This can be removed once the upstream types for the event handler
            // have been fixed
          } as unknown as SearchFormSubmitData

          if (formRadiusCentre) {
            trackClientSideOptimizelyEvent(
              OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEARCH_FORM_SUBMITTED,
              optimizelyDecisionsContext,
            )
          }

          trackClientSideOptimizelyEvent(
            OPTIMIZELY_EVENTS.SEARCH_FORM_SUBMITTED,
            optimizelyDecisionsContext,
          )

          handleSearchFormSubmit(
            brand,
            formDataWithDestinationURL,
            tms,
            placesConfig,
          )
        }}
        onSearchFormSummaryClick={() => handleSearchFormSummaryClick(tms)}
        useLegacyDestinationSelector={useLegacyDestinationSelector}
        legacyListWithPlaces={{
          enabled: useLegacyDestinationSelector,
          data: placesLegacyListData,
        }}
        onAutocompleteSelected={(destinationId, source) =>
          // This is fine for now as 'undefined' passed into the includes just returns false,
          // which is proably what we want. Probably need to modify the upstream types
          ['google-textsearch', 'google-autocomplete'].includes(
            source as string,
          )
            ? setGoogleID(destinationId)
            : null
        }
        locale={locale}
        {...(driveTimeSearchEnabled
          ? {}
          : /* GD-73: Drive Time Search - only pass these handlers when experiment is disabled otherwise get TS error */
            {
              onDestinationSelectionEvent: handleDestinationSelection,
              onDateSelectionEvent: handleDateSelection,
              onGuestSelectionEvent: handleGuestSelection,
            })}
      />
    </div>
  )

  const { displayMode, setDisplayMode } = useDisplayContext()
  const setDisplayModeWithTms = setDisplayModeWrapper(tms, setDisplayMode)
  const searchControls = () => {
    let sortOptionsFromConfig = getSortOptionsFromConfig({
      accommodationType,
      brand,
      startDate: start,
    })

    const isDatedSearch = Boolean(start)

    if (isDatedSearch) {
      sortOptionsFromConfig = [
        SORT_OPTIONS_ENUM.RECOMMENDED,
        SORT_OPTIONS_ENUM.PRICE_ASCENDING,
        SORT_OPTIONS_ENUM.PRICE_DESCENDING,
        SORT_OPTIONS_ENUM.SLEEPS_ASCENDING,
        SORT_OPTIONS_ENUM.SLEEPS_DESCENDING,
        SORT_OPTIONS_ENUM.REVIEWS_DESCENDING,
        SORT_OPTIONS_ENUM.NO_SORT,
      ]
    } else {
      sortOptionsFromConfig = [
        SORT_OPTIONS_ENUM.RECOMMENDED,
        SORT_OPTIONS_ENUM.NO_SORT,
        SORT_OPTIONS_ENUM.SLEEPS_ASCENDING,
        SORT_OPTIONS_ENUM.SLEEPS_DESCENDING,
        SORT_OPTIONS_ENUM.REVIEWS_DESCENDING,
      ]
    }

    const displaySortOptions = shouldDisplaySortOptions({
      displayMode,
    })

    const sortOptions = sortOptionsFromConfig.map((option) => ({
      text: formatMessage({
        id: `search-controls.sort.option-names.${option}`,
      }),
      value: option,
    }))

    return (
      <SearchControls
        filtersCount={totalSelectedFilters?.toString() || '0'}
        sorting={
          displaySortOptions
            ? {
                sortOptions,
                labels: {
                  selectOption: formatMessage({
                    id: 'search-controls.sort.select-option',
                    defaultMessage: 'Select sort option',
                  }),
                  selectedOptionPrefix: formatMessage({
                    id: 'search-controls.sort.selected-option-prefix',
                    defaultMessage: 'Sort',
                  }),
                },
                selectedOption: sort ?? '',
                onOptionChange: (sort: string) =>
                  dispatch({ type: SORT_ORDER_UPDATED, sort }),
              }
            : undefined
        }
        showFilters={openFilters}
        justify={['space-evenly', 'space-evenly', 'space-evenly']}
        shouldHideSortDisplayText={true}
        displayMode={displayMode}
        setDisplayMode={setDisplayModeWithTms}
        translations={{
          filterTitle: formatMessage({
            id: 'search-controls.filter.title',
            defaultMessage: 'Filters',
          }),
          filterAriaText: formatMessage({
            id: 'search-controls.filter.aria',
            defaultMessage: 'Open filters',
          }),
          listTitle: formatMessage({
            id: 'search-controls.list.title',
            defaultMessage: 'Back to list view',
          }),
          listAriaText: formatMessage({
            id: 'search-controls.list.aria',
            defaultMessage: 'Back to list view',
          }),
          mapTitle: formatMessage({
            id: 'search-controls.map.title',
            defaultMessage: 'Map',
          }),
          mapAriaText: formatMessage({
            id: 'search-controls.map.aria',
            defaultMessage: 'Map View',
          }),
        }}
      />
    )
  }

  if (mobileStickyMode) {
    return (
      <MobileSticky isGuestApp={isGuestApp}>
        {({ stuck }) => (
          <div
            /*  GD-369 Hack - adding correct gradient for James Villas */
            id={
              brand === 'jamesVillas'
                ? 'james-villas-shadow-hack-wrapper-GD-369'
                : undefined
            }
          >
            <StickableHeaderExtended stuck={stuck} hasSeparator>
              <Container>{searchForm(stuck)}</Container>
            </StickableHeaderExtended>
            {hasResults && (
              <MobileStickableContent stuck={stuck}>
                {searchControls()}
              </MobileStickableContent>
            )}
          </div>
        )}
      </MobileSticky>
    )
  }

  return (
    <div>
      <Container>{searchForm(true)}</Container>
      {(hasResults || displayMode === 'MAP') && <>{searchControls()}</>}
    </div>
  )
}
